import React from "react";
import styles from "./smartFilterAppliedPills.module.scss";
import useSmartFilter from "../useSmartFilter";
import wrapperHOC from "@Utils/wrapperHOC";
import { useSelector } from "react-redux";
import { AppState } from "@Store/index";
import { getCurrencyFromRegion } from "@Components/LeadFormModal/utils";
import { getPricingCurrency } from "@Pages/Search/components/SearchListItem/helper";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { COUNTRIES_WITH_MONTHLY_DURATION, PAGE } from "constants/configConstants";
import {
  ROOM_TYPE_FILTER,
  ROOM_TYPE_SPECIFIC,
  SHARING_OPTIONS_BATHROOM,
  SHARING_OPTIONS_KITCHEN,
  STAY_DURATION_COMMON,
  STAY_DURATION_US,
} from "@Pages/Search/components/FilterSection/data/filtersList";
import { CloseIcon } from "../icons";

/**
 * Displays the extracted and applied filter values as removable pills
 * with pink background and close (X) icons.
 * Clicking X removes the filter and re-fetches inventory count.
 */
const FILTER_DISPLAY_MAP: Record<string, string> = {
  sort_key: "Sort",
  range_available_price: "Budget",
  meta_types: "Room Type",
  meta_unit_types: "Property Type",
  move_in_month: "Move In",
  meta_min_lease_duration: "Min Lease",
  meta_max_lease_duration: "Max Lease",
  tags: "Tags",
  amenities: "Amenities",
  guarantor: "Guarantor",
  university: "University",
  locality: "Locality",
  region_radius: "Distance",
  available: "Availability",
};

/**
 * Builds a value → display name lookup from the room type filter lists.
 */
const ROOM_TYPE_LABEL_MAP: Record<string, string> = [
  ...ROOM_TYPE_SPECIFIC.list,
  ...ROOM_TYPE_FILTER.list,
  ...SHARING_OPTIONS_BATHROOM.list,
  ...SHARING_OPTIONS_KITCHEN.list,
].reduce((map, item) => {
  map[item.value] = item.name;
  return map;
}, {} as Record<string, string>);

/**
 * Maps sort_key + sort_order combinations to human-readable labels.
 */
const SORT_LABEL_MAP: Record<string, string> = {
  "available_price,asc": "Price (Low to High)",
  "available_price,desc": "Price (High to Low)",
  "distance,asc": "Nearest",
  "distance,desc": "Nearest",
  "created,desc": "Newly Added",
  "created,asc": "Newly Added",
};

/**
 * Formats the display value for a filter pill.
 * Budget is formatted as "£32 – £400" with currency symbol.
 * Room types are mapped to their human-readable labels.
 */
const formatPillValue = (key: string, value: any, currency: string, sortOrder?: string): string => {
  if (key === "range_available_price" && typeof value === "string") {
    const [min, max] = value.split(",");
    if (min && max) return `${currency}${min} – ${currency}${max}`;
    if (min && !max) return `${currency}${min}+`;
    if (!min && max) return `Up to ${currency}${max}`;
  }
  if (key === "meta_types" && typeof value === "string") {
    return value
      .split(",")
      .map((v: string) => ROOM_TYPE_LABEL_MAP[v.trim()] || v.trim())
      .join(", ");
  }
  if (key === "move_in_month" && typeof value === "string") {
    // Convert "sep-2026" to "Sep, 2026"
    const [month, year] = value.split("-");
    if (month && year) {
      return `${month.charAt(0).toUpperCase()}${month.slice(1)}, ${year}`;
    }
  }
  if (key === "sort_key" && typeof value === "string") {
    const sortCombo = `${value},${sortOrder || "desc"}`;
    return SORT_LABEL_MAP[sortCombo] || value;
  }
  if (key === "tags" && typeof value === "string") {
    // Convert "dual_occupancy" to "Dual Occupancy"
    return value
      .split(",")
      .map((v: string) =>
        v
          .trim()
          .split("_")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" "),
      )
      .join(", ");
  }
  if (key === "region_radius") {
    const meters = Number(value);
    if (meters <= 1609) return "Up to 1 Mile";
    if (meters <= 3218) return "Up to 2 Miles";
    if (meters <= 8046) return "Up to 5 Miles";
    return `Up to ${Math.round(meters / 1609)} Miles`;
  }
  if (key === "amenities" && typeof value === "string") {
    return value
      .split(",")
      .map((v: string) =>
        v
          .trim()
          .split("_")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" "),
      )
      .join(", ");
  }
  if (key === "meta_unit_types" && typeof value === "string") {
    return value
      .split(",")
      .map((v: string) =>
        v
          .trim()
          .split("_")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" "),
      )
      .join(", ");
  }
  return String(value);
};

const SmartFilterAppliedPills: React.FC = () => {
  const { appliedFilters, removeFilter } = useSmartFilter();
  const region = useSelector(({ searchPageData }: AppState) => searchPageData.region);
  const regionCurrency = getCurrencyFromRegion(region?.combined_name);
  const currency = getPricingCurrency(regionCurrency) || "£";

  if (!appliedFilters) return null;

  // Combine min/max lease duration into a single entry
  const hasMinLease = !!appliedFilters.meta_min_lease_duration;
  const hasMaxLease = !!appliedFilters.meta_max_lease_duration;
  const hasLeaseDuration = hasMinLease || hasMaxLease;

  const pillEntries = Object.entries(appliedFilters).filter(
    ([key, value]) =>
      value &&
      key !== "is_irrelevant" &&
      key !== "sort_order" &&
      key !== "university_canonical_name" &&
      key !== "locality_canonical_name" &&
      key !== "meta_min_lease_duration" &&
      key !== "meta_max_lease_duration",
  );

  if (pillEntries.length === 0 && !hasLeaseDuration) return null;

  const handleRemoveLeaseDuration = () => {
    if (hasMinLease) removeFilter("meta_min_lease_duration");
    if (hasMaxLease) removeFilter("meta_max_lease_duration");
    gtagEventEmitter({
      action: "smart_filter_pill_removed",
      category: PAGE.SEARCH,
      label: "lease_duration",
    });
  };

  const getLeaseDurationLabel = (): string => {
    const min = appliedFilters.meta_min_lease_duration || "0";
    const max = appliedFilters.meta_max_lease_duration || "";
    const countryShortName = region?.location?.country?.short_name || "";
    const isMonthly = COUNTRIES_WITH_MONTHLY_DURATION.includes(countryShortName);

    const unit = isMonthly ? "months" : "weeks";
    const durationOptions = isMonthly ? STAY_DURATION_US : STAY_DURATION_COMMON;
    const matchValue = `${min} ${unit},${max ? `${max} ${unit}` : ""}`;

    const matched = durationOptions.find((opt) => opt.value === matchValue);
    if (matched && matched.name !== "All") return matched.name;

    // Fallback
    const fallbackUnit = isMonthly ? "Months" : "Weeks";
    if (max) return `${min} - ${max} ${fallbackUnit}`;
    return `${min}+ ${fallbackUnit}`;
  };

  return (
    <div className={styles.container}>
      {hasLeaseDuration && (
        <div className={styles.pill}>
          <span className={styles.pillLabel}>Lease Duration : {getLeaseDurationLabel()}</span>
          <span
            className={styles.pillClose}
            role="button"
            tabIndex={0}
            aria-label="Remove Lease Duration filter"
            onClick={handleRemoveLeaseDuration}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") handleRemoveLeaseDuration();
            }}
          >
            <CloseIcon />
          </span>
        </div>
      )}
      {pillEntries.map(([key, value]) => (
        <div key={key} className={styles.pill}>
          <span className={styles.pillLabel}>
            {FILTER_DISPLAY_MAP[key] || key} :{" "}
            {formatPillValue(key, value, currency, appliedFilters.sort_order)}
          </span>
          <span
            className={styles.pillClose}
            role="button"
            tabIndex={0}
            aria-label={`Remove ${FILTER_DISPLAY_MAP[key] || key} filter`}
            onClick={() => {
              removeFilter(key);
              gtagEventEmitter({
                action: "smart_filter_pill_removed",
                category: PAGE.SEARCH,
                label: key,
              });
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                removeFilter(key);
                gtagEventEmitter({
                  action: "smart_filter_pill_removed",
                  category: PAGE.SEARCH,
                  label: key,
                });
              }
            }}
          >
            <CloseIcon />
          </span>
        </div>
      ))}
    </div>
  );
};

export default wrapperHOC(SmartFilterAppliedPills, {
  componentName: "SmartFilterAppliedPills",
  showForChina: false,
  showForWhiteLable: true,
});
